import { useQueryClient } from '@tanstack/react-query';

import {
  getCompaniesList,
  getCompanyById,
  getCompanyChannels,
  getCompanyCrmUsers,
  getCompanyCrmRouting,
  getCompanyModules,
  getCompanyOutboundPolicyEvents,
  getCompanyUsers,
  CompanyModulesResponseDto,
  CompanyOutboundPolicyEventsFiltersDto,
  ICompanyDto,
  ICompanyResponseDto,
  IUserOfCompanyResponseDto,
} from '@/shared/api/companies';

const COMPANY_QUERY_KEY = 'company';

const normalizeCompany = <T extends ICompanyDto>(company: T): T => ({
  ...company,
  owner_id: company.owner_id ?? company.ownerId ?? '',
});

export const companiesListQuery = () => ({
  queryKey: [COMPANY_QUERY_KEY, 'list'],
  queryFn: (): Promise<ICompanyDto[]> =>
    getCompaniesList().then((r) => r.map(normalizeCompany)),
});

export const companyByIdQuery = (id: string) => ({
  queryKey: [COMPANY_QUERY_KEY, 'byId', id],
  queryFn: (): Promise<ICompanyResponseDto> =>
    getCompanyById(id).then(normalizeCompany),
});

export const usersOfCompanyListQuery = (companyId: string) => ({
  queryKey: [COMPANY_QUERY_KEY, companyId, 'users'],
  queryFn: () =>
    getCompanyUsers(companyId).then((r) => r as IUserOfCompanyResponseDto[]),
});

export const channelsOfCompanyListQuery = (companyId: string) => ({
  queryKey: [COMPANY_QUERY_KEY, companyId, 'channels'],
  queryFn: () => getCompanyChannels(companyId).then((r) => r),
});

export const companyCrmRoutingQuery = (companyId: string) => ({
  queryKey: [COMPANY_QUERY_KEY, companyId, 'crm-routing'],
  queryFn: () => getCompanyCrmRouting(companyId),
});

export const companyCrmUsersQuery = (companyId: string) => ({
  queryKey: [COMPANY_QUERY_KEY, companyId, 'crm-users'],
  queryFn: () => getCompanyCrmUsers(companyId),
});

export const companyModulesQuery = (companyId: string) => ({
  queryKey: [COMPANY_QUERY_KEY, companyId, 'modules'],
  queryFn: (): Promise<CompanyModulesResponseDto> => getCompanyModules(companyId),
});

export const companyOutboundPolicyEventsQuery = (
  companyId: string,
  filters: CompanyOutboundPolicyEventsFiltersDto = {}
) => ({
  queryKey: [COMPANY_QUERY_KEY, companyId, 'outbound-policy-events', filters],
  queryFn: () => getCompanyOutboundPolicyEvents(companyId, filters),
});

export const useInvalidateCompaniesList = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [COMPANY_QUERY_KEY, 'list'],
    });
};

export const useInvalidateCompanyById = () => {
  const queryClient = useQueryClient();

  return (companyId: string) =>
    queryClient.invalidateQueries({
      queryKey: [COMPANY_QUERY_KEY, 'byId', companyId],
    });
};

export const useOptimisticUpdateCompanyById = () => {
  const queryClient = useQueryClient();

  return (data: ICompanyDto) =>
    queryClient.setQueryData(['company', 'byId', data.id], { ...data });
};

export const useInvalidateUsersOfCompanyList = (companyId: string) => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [COMPANY_QUERY_KEY, companyId, 'users'],
    });
};

export const useInvalidateChannelsOfCompanyList = () => {
  const queryClient = useQueryClient();

  return (companyId: string) =>
    queryClient.invalidateQueries({
      queryKey: [COMPANY_QUERY_KEY, companyId, 'channels'],
    });
};

export const useInvalidateAllCompanyChannels = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [COMPANY_QUERY_KEY],
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === COMPANY_QUERY_KEY &&
        query.queryKey[2] === 'channels',
    });
};

export const useInvalidateCompanyCrmRouting = () => {
  const queryClient = useQueryClient();

  return (companyId: string) =>
    queryClient.invalidateQueries({
      queryKey: [COMPANY_QUERY_KEY, companyId, 'crm-routing'],
    });
};
